/* Virtual try-on - PE (contact-lenses PDP, design row L1, 2026-10-05) — snippets/paex-tryon.liquid.
 *
 * Auglio's app-embed script finds every .auglio-tryon-btn, asks its API which data-item_id values are
 * published in Magic Mirror and sets style.display = 'block' on those (the click is handled by Auglio's
 * listener on document). Every refresh first sets display:none on all buttons, then re-shows the published
 * ones and dispatches 'auglioButtonsRefreshed' on document. This element never reveals the button itself:
 *  1. placement on_image: moves the button into the main-image viewport (the gallery's .slider-container —
 *     the box the wishlist heart and the C34 occasion pill use; it does not translate with the slides — or the
 *     first media item in the stack layout). No anchor found → the pill stays inline.
 *  2. MutationObserver on the button's style: the moment Auglio shows it, the pill ([data-paex-tryon-on]),
 *     the wrapper and the prompt row show. They hide again only on a SETTLED "no" (Auglio's refresh event with
 *     the button still hidden, or no answer at all 6 s after load) — Auglio's hide-then-recheck refreshes
 *     (its legacy embed runs a second one) would otherwise flash the pill and shift the page twice.
 *  3. A settled "yes" is remembered per variant (localStorage); the snippet's inline script then shows the row
 *     before first paint on the next view, so the page does not shift when Auglio answers.
 *  4. variantChange → data-item_id = the new variant id, then Auglio re-checks (auglioRefreshBtns).
 *  5. the prompt row's link clicks the same Auglio button (one .auglio-tryon-btn on the page).
 */
if (!customElements.get('paex-tryon')) {
  customElements.define(
    'paex-tryon',
    class PaexTryon extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('.paex-tryon__btn');
        if (!this.btn) return;
        this.row = this.querySelector('[data-paex-tryon-row]');
        this.preview = this.hasAttribute('data-editor-preview');
        this.on = false;
        this.placed = false;
        if (this.dataset.placement === 'on_image') this.place();

        this.onOpen = (event) => {
          event.preventDefault();
          if (!this.btn) return;
          if (this.revealed()) this.btn.click();
          else this.pendingOpen = true; // cached row clicked before Auglio answered: open on reveal
        };
        this.querySelectorAll('[data-paex-tryon-open]').forEach((el) => el.addEventListener('click', this.onOpen));

        this.observer = new MutationObserver(() => {
          if (this.revealed()) this.settle(true);
        });
        this.observer.observe(this.btn, { attributes: true, attributeFilter: ['style'] });

        this.onRefreshed = () => this.settle(this.revealed(), true);
        document.addEventListener('auglioButtonsRefreshed', this.onRefreshed);

        // no answer from Auglio (blocked script, API down): fall back to the button's own state
        const fallback = () => {
          this.fallbackTimer = setTimeout(() => this.settle(this.revealed(), true), 6000);
        };
        if (document.readyState === 'complete') fallback();
        else window.addEventListener('load', fallback, { once: true });

        // typeof-gate: webvista / PUB_SUB_EVENTS are global.js lexical consts, not window properties
        if (typeof webvista !== 'undefined' && webvista.subscribe && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = webvista.subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data;
            if (!data || !data.variant || String(data.sectionId) !== this.dataset.section) return;
            this.setItem(data.variant.id);
          });
        }
        this.settle(this.revealed() || this.hasAttribute('data-paex-tryon-cached'));
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        clearTimeout(this.fallbackTimer);
        if (this.onRefreshed) document.removeEventListener('auglioButtonsRefreshed', this.onRefreshed);
        if (this.onOpen) {
          this.querySelectorAll('[data-paex-tryon-open]').forEach((el) => el.removeEventListener('click', this.onOpen));
        }
        // the moved pill belongs to this block: never leave an orphan .auglio-tryon-btn behind
        if (this.placed && this.btn && !this.contains(this.btn)) this.btn.remove();
      }

      place() {
        const root = document.getElementById('MainProduct-' + this.dataset.section);
        const gallery = root && root.querySelector('.product-media-gallery .media-gallery');
        if (!gallery) return;
        const anchor = gallery.hasAttribute('data-media-stack')
          ? gallery.querySelector('.product-media-item')
          : gallery.querySelector(':scope > .product-media-slider > .slider-container');
        if (!anchor) return;
        this.btn.setAttribute('data-paex-tryon-placed', '');
        anchor.appendChild(this.btn);
        this.placed = true;
      }

      // Auglio's own verdict: its inline display is no longer "none"
      revealed() {
        return !/none/.test(this.btn.getAttribute('style') || '');
      }

      settle(state, final) {
        if (final) clearTimeout(this.fallbackTimer);
        const on = this.preview || state;
        // the pill itself only ever shows after Auglio revealed the button (a cached row does not show it)
        this.btn.toggleAttribute('data-paex-tryon-on', this.preview || (on && this.revealed()));
        if (on !== this.on || final) {
          this.on = on;
          if (this.row) this.row.hidden = !on;
          // a placed pill lives in the gallery: the wrapper then only carries the row
          const show = on && (!this.placed || Boolean(this.row));
          if (this.hidden === show) this.hidden = !show;
        }
        if (final && !this.preview) this.remember(state);
        if (this.pendingOpen && this.revealed()) {
          this.pendingOpen = false;
          this.btn.click();
        }
      }

      remember(state) {
        try {
          const key = 'paex-tryon:' + this.btn.dataset.item_id;
          if (state) localStorage.setItem(key, '1');
          else localStorage.removeItem(key);
        } catch (e) {
          /* storage blocked: no memory, same behaviour */
        }
      }

      setItem(id) {
        if (!id || !this.btn || this.btn.dataset.item_id === String(id)) return;
        this.btn.dataset.item_id = String(id);
        // Auglio hides every button, re-asks its API, re-shows the published ones (app-embed global)
        if (typeof auglioRefreshBtns === 'function') auglioRefreshBtns();
      }
    },
  );
}
